import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isTruthy} from 'ramda-adjunct';

import {
  COLUMN_SEPARATOR_STYLE,
  PINNED_LEFT_BOUNDARY_LINE_STYLE,
  PINNED_LEFT_EDGE_LINE_STYLE,
  PINNED_RIGHT_EDGE_LINE_STYLE,
} from '../../constants/borderStyles';
import {DEFAULT_COL_WIDTH} from '../../constants/defaultColWidth';
import {
  selectSummaryRowCells,
  selectVerticalSeparatorsVisible,
} from '../../store/createEagGridStore';
import {useGridSettingsSafe} from '../../store/selectors';
import {useEagGridSelector} from '../../store/useEagGridSelector';
import type {CellsV5} from '../../types/Api';
import type {EagColumn} from '../../types/EagColumn';
import type {EagColumnState} from '../../types/EagColumnState';
import type {RowData} from '../../types/RowData';
import {getPinnedEdgeShadowClass} from '../../utils/getPinnedEdgeShadowClass';
import {getValueFromRawCell} from '../../utils/getValueFromRawCell';
import {getRowPaddingClass, LineHeightToPx} from '../../utils/lineHeightToValue';
import {renderCell} from '../../utils/renderCell';

export type SummaryRowProps = {
  columnStateMap: Map<string, EagColumnState>;
  nonRightPinnedColumns: EagColumn[];
  rightPinnedColumns: EagColumn[];
  visibleColumnIndexMap: Map<string, number>;
  pinnedLeftOffsets: Map<string, number>;
  pinnedRightOffsets: Map<string, number>;
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
  actionColumnWidth?: number;
  hasCheckboxColumn: boolean;
};

const SUMMARY_ROW_BORDER_TOP = {borderTop: '2px solid #DFE1E6'} as const;

/** Synthetic RowData for renderCell compatibility — summary row has no id/actions. */
const SUMMARY_DUMMY_ROW: RowData = {
  id: '__summary__',
  rowNumber: '',
  actions: {primary: [], secondary: []},
};

export function SummaryRow(props: SummaryRowProps) {
  const summaryRowCells = useEagGridSelector(selectSummaryRowCells);
  const verticalSeparators = useEagGridSelector(selectVerticalSeparatorsVisible);
  const gridSettings = useGridSettingsSafe();
  const heightPx = LineHeightToPx[gridSettings.rowHeight];
  const paddingClass = getRowPaddingClass[gridSettings.rowHeight];

  if (!summaryRowCells) {
    return null;
  }

  // Capture the narrowed non-null value so the inner function can access it without assertion
  const cells = summaryRowCells;

  function renderSummaryCell(column: EagColumn) {
    const columnState = props.columnStateMap.get(column.key);
    if (!columnState) {
      return null;
    }

    const width = columnState.width ?? DEFAULT_COL_WIDTH;
    const rawValue = cells[column.field] as CellsV5 | undefined;
    const value = getValueFromRawCell(column, rawValue);

    const isLastPinnedLeft = props.lastPinnedLeftKey === column.key;
    const isFirstPinnedRight = props.firstPinnedRightKey === column.key;
    const pinnedEdgeShadowClass = getPinnedEdgeShadowClass(
      columnState.pinned,
      isLastPinnedLeft || isFirstPinnedRight
    );
    const isLastColumn =
      props.visibleColumnIndexMap.get(column.key) === props.visibleColumnIndexMap.size - 1;
    const isPinned = columnState.pinned === 'LEFT' || columnState.pinned === 'RIGHT';

    const pinnedStyle = match(columnState.pinned)
      .with('LEFT', () => ({
        position: 'sticky' as const,
        left: props.pinnedLeftOffsets.get(column.key) ?? 0,
        zIndex: 'var(--z-sidebar)',
      }))
      .with('RIGHT', () => ({
        position: 'sticky' as const,
        right: props.pinnedRightOffsets.get(column.key) ?? 0,
        zIndex: 'var(--z-sidebar)',
      }))
      .otherwise(() => undefined);

    // Pinned cells need explicit bg + hover override (same as data GridRow)
    const bgClass = isPinned ? 'bg-[#F4F5F7] group-hover/summaryrow:bg-[#EBECF0]' : 'bg-inherit';
    const shadowClass = pinnedEdgeShadowClass ?? '';

    return (
      <td
        key={column.key}
        className={`relative overflow-hidden ${paddingClass} text-ellipsis whitespace-nowrap ${bgClass} ${shadowClass} font-semibold`}
        style={{
          width,
          maxWidth: width,
          height: heightPx,
          maxHeight: heightPx,
          ...pinnedStyle,
        }}
        data-column={column.key}
      >
        {value != null ? renderCell(column, value, SUMMARY_DUMMY_ROW) : null}
        {/* Elevation shade on the last left-pinned column — the pinned/scrollable boundary. */}
        <Show when={isPinned && columnState.pinned === 'LEFT'}>
          <span
            style={isLastPinnedLeft ? PINNED_LEFT_BOUNDARY_LINE_STYLE : PINNED_LEFT_EDGE_LINE_STYLE}
          />
        </Show>
        <Show when={isPinned && columnState.pinned === 'RIGHT'}>
          <span style={PINNED_RIGHT_EDGE_LINE_STYLE} />
        </Show>
        <Show when={verticalSeparators && !isPinned && !isLastColumn}>
          <span style={COLUMN_SEPARATOR_STYLE} />
        </Show>
      </td>
    );
  }

  return (
    <tfoot className="sticky bottom-0 z-[var(--z-sidebar)]">
      <tr
        className="group/summaryrow bg-[#F4F5F7] hover:bg-[#EBECF0]"
        style={{
          height: heightPx,
          ...SUMMARY_ROW_BORDER_TOP,
        }}
      >
        {/* Checkbox column spacer */}
        <Show when={props.hasCheckboxColumn}>
          <td className="bg-[#F4F5F7] group-hover/summaryrow:bg-[#EBECF0] sticky left-0 z-[var(--z-sidebar)]" />
        </Show>
        {/* Left-pinned + unpinned cells */}
        {props.nonRightPinnedColumns.map(renderSummaryCell)}
        {/* Filler td */}
        <td className="bg-inherit" />
        {/* Right-pinned cells */}
        {props.rightPinnedColumns.map(renderSummaryCell)}
        {/* Action column spacer */}
        <Show when={isTruthy(props.actionColumnWidth)}>
          <td
            className="bg-[#F4F5F7] group-hover/summaryrow:bg-[#EBECF0] sticky right-0 z-[var(--z-sidebar)]"
            style={{width: props.actionColumnWidth, minWidth: props.actionColumnWidth}}
          />
        </Show>
      </tr>
    </tfoot>
  );
}
